<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script lang="ts">
import type { PopoverContentEmits, PopoverContentProps } from 'reka-ui'

import { ButtonColor, ButtonLook } from './button/SimpleButton.vue'

export interface PopoverProps extends PopoverContentProps {
  modal?: boolean
  noIcon?: boolean
  iconButton?: boolean
  buttonAriaLabel?: string
  triggerAsChild?: boolean
  class?: string
}

export type PopoverEmits = PopoverContentEmits

export type PopoverInstance = {
  openPopover: () => void
}

type PopoverSlots = {
  trigger: () => unknown
  header?: () => unknown
  default: () => unknown
}
</script>

<script setup lang="ts">
import {
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
  useForwardExpose,
} from 'reka-ui'
import { computed } from 'vue'

import { useForwardPropsEmitsSkip } from '@/composables/utils/useForwardPropsEmitsSkip.ts'

import SimpleButton from './button/SimpleButton.vue'

import ChevronDown from '~mingcute/arrow/down_line.svg'
import Clear from '~mingcute/system/close_line.svg'

const open = defineModel<boolean>('open')
const props = defineProps<PopoverProps>()
const emit = defineEmits<PopoverEmits>()
const slots = defineSlots<PopoverSlots>()

const forwarded = useForwardPropsEmitsSkip(
  props,
  emit,
  ['modal', 'noIcon', 'buttonAriaLabel', 'class', 'triggerAsChild'],
  ['open']
)

function openPopover() {
  open.value = true
}

const isIconButton = computed(() => props.iconButton || !slots.trigger)

defineExpose<PopoverInstance>({
  openPopover,
})
const { forwardRef } = useForwardExpose()
</script>

<template>
  <PopoverRoot v-model:open="open" :modal="modal">
    <PopoverTrigger :ref="forwardRef" as-child>
      <SimpleButton
        v-if="!triggerAsChild"
        :color="open ? ButtonColor.ACCENT : ButtonColor.PRIMARY"
        :look="ButtonLook.OUTLINED"
        :icon-button="isIconButton"
        :aria-label="buttonAriaLabel"
        class="flopui-popover-trigger"
      >
        <slot name="trigger" />
        <ChevronDown v-if="noIcon !== true" class="flopui-popover-icon" aria-hidden="true" />
      </SimpleButton>
      <slot v-else name="trigger" />
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        v-bind="forwarded"
        :side="side || 'bottom'"
        :collision-padding="8"
        class="flopui-popover-content"
        :class="$props.class"
      >
        <div v-if="$slots.header" class="flopui-popover-header">
          <slot name="header" />
        </div>

        <div class="flopui-popover-body">
          <slot />
        </div>

        <PopoverClose class="flopui-popover-close" :aria-label="$t('uikit.common.close')">
          <Clear />
        </PopoverClose>
        <PopoverArrow class="flopui-popover-arrow" />
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style>
.flopui-popover-trigger .flopui-popover-icon {
  transition: transform 0.3s;
}

.flopui-popover-trigger[data-state='open'] .flopui-popover-icon {
  transform: rotate(180deg);
}

.flopui-popover-content {
  border-radius: var(--border-radius);
  width: 260px;
  background-color: var(--color-background-primary);
  box-shadow: var(--shadow-strong);
}

.flopui-popover-content:focus {
  outline: 2px var(--color-outline) solid;
}

.flopui-popover-content:focus .flopui-popover-arrow {
  filter: drop-shadow(0 2px 0 var(--color-outline)) drop-shadow(var(--shadow-strong));
}

@media (prefers-reduced-motion: no-preference) {
  .flopui-popover-content {
    animation-duration: 0.4s;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity;
  }

  .flopui-popover-content[data-state='open'][data-side='top'] {
    animation-name: flopui-slide-down-and-fade-in;
  }

  .flopui-popover-content[data-state='open'][data-side='right'] {
    animation-name: flopui-slide-left-and-fade-in;
  }

  .flopui-popover-content[data-state='open'][data-side='bottom'] {
    animation-name: flopui-slide-up-and-fade-in;
  }

  .flopui-popover-content[data-state='open'][data-side='left'] {
    animation-name: flopui-slide-right-and-fade-in;
  }
}

.flopui-popover-arrow {
  fill: var(--color-background-primary);
  filter: drop-shadow(var(--shadow-strong));
}

.flopui-popover-header {
  border-bottom: 1px var(--color-separator) solid;
  padding: 0.75rem 2.5rem 0.75rem 0.875rem;
  font-weight: 500;
}

[dir='rtl'] .flopui-popover-header {
  padding: 0.75rem 0.875rem 0.75rem 2.5rem;
}

.flopui-popover-body {
  font-size: 0.875em;
  padding: 0.75em;
}

.flopui-popover-close {
  all: unset;
  box-sizing: border-box;
  min-height: 0;
  min-width: 0;
  flex-shrink: 0;

  border-radius: 50%;
  height: 1.5rem;
  width: 1.5rem;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0.5625rem;
  right: 0.75rem;
  color: var(--color-text-muted);
  cursor: pointer;
}

[dir='rtl'] .flopui-popover-close {
  right: unset;
  left: 0.75rem;
}

.flopui-popover-close:hover,
.flopui-popover-close:focus {
  color: var(--color-text-primary);
  background-color: var(--color-background-danger-light);
}

.flopui-popover-close:focus-visible {
  outline: 2px var(--color-background-danger) solid;
}

@media (prefers-reduced-motion: no-preference) {
  .flopui-popover-close {
    transition:
      background-color 0.2s,
      color 0.2s;
  }
}
</style>
